import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { GalleryHorizontal, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  useHeroSlidesAdmin,
  useCreateHeroSlide,
  useUpdateHeroSlide,
  useDeleteHeroSlide,
} from '@/lib/productCatalog'
import { productMediaApi } from '@/api/productMedia'
import { userMessage } from '@/api/errors'
import type { HeroSlide, HeroSlideInput } from '@/types/ecommerce'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { resolveMediaUrl } from '@/utils/mediaUrl';

const fields: EditField[] = [
  {
    key: 'imageUrl',
    label: 'Image',
    required: true,
    type: 'file',
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    previewKind: 'image',
    uploadFn: (file) => productMediaApi.uploadHeroSlideImage(file),
    hint: 'JPEG, PNG, GIF or WEBP, up to 8MB.',
  },
  {
    key: 'videoUrl',
    label: 'Video (optional)',
    type: 'file',
    accept: 'video/mp4,video/webm,video/quicktime',
    previewKind: 'video',
    uploadFn: (file) => productMediaApi.uploadHeroSlideVideo(file),
    hint: 'MP4, WEBM or MOV, up to 100MB.',
  },
  { key: 'title', label: 'Title' },
  { key: 'subtitle', label: 'Subtitle', type: 'textarea' },
  { key: 'ctaText', label: 'Button text', placeholder: 'Shop now' },
  { key: 'ctaLink', label: 'Button link', placeholder: '/products' },
  { key: 'sortOrder', label: 'Sort order', type: 'number', required: true },
  { key: 'active', label: 'Active', type: 'checkbox', placeholder: 'Show on the storefront homepage' },
]

const toInput = (values: Record<string, any>): HeroSlideInput => ({
  imageUrl: values.imageUrl,
  videoUrl: values.videoUrl || undefined,
  title: values.title || undefined,
  subtitle: values.subtitle || undefined,
  ctaText: values.ctaText || undefined,
  ctaLink: values.ctaLink || undefined,
  sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
  active: !!values.active,
})

function HeroSlidesPage() {
  const { data: slides = [], isLoading, isError, error, isFetching, refetch } = useHeroSlidesAdmin()
  const create = useCreateHeroSlide()
  const update = useUpdateHeroSlide()
  const del = useDeleteHeroSlide()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<HeroSlide | null>(null)

  const sorted = [...slides].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <GalleryHorizontal className="w-6 h-6" /> Hero Slides
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Homepage carousel shown on the ecommerce storefront. Ordered by sort order; only active slides appear.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Slide
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading slides…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load hero slides: {(error as Error)?.message}.</p>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-gray-500">No hero slides yet — create one to populate the storefront carousel.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Preview</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">CTA</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr key={s.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <img src={resolveMediaUrl(s.imageUrl)} alt="" className="h-12 w-20 rounded-md object-cover bg-gray-100 dark:bg-gray-700" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{s.title || '—'}</div>
                    <div className="text-xs text-gray-500 max-w-xs truncate">{s.subtitle}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {s.ctaText ? `${s.ctaText} → ${s.ctaLink || '—'}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.sortOrder}</td>
                  <td className="px-4 py-3">
                    <InlineToggle
                      checked={s.active}
                      onToggle={(next) =>
                        update.mutateAsync({
                          id: s.id,
                          body: {
                            imageUrl: s.imageUrl,
                            videoUrl: s.videoUrl,
                            title: s.title,
                            subtitle: s.subtitle,
                            ctaText: s.ctaText,
                            ctaLink: s.ctaLink,
                            sortOrder: s.sortOrder,
                            active: next,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(s)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit slide">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(s.title || 'this slide', async () => {
                            await del.mutateAsync(s.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete slide"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Hero Slide"
        fields={fields}
        initialValues={{ sortOrder: slides.length, active: true }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Hero Slide"
        description={editing?.title}
        fields={fields}
        initialValues={
          editing
            ? {
                imageUrl: editing.imageUrl,
                videoUrl: editing.videoUrl ?? '',
                title: editing.title,
                subtitle: editing.subtitle,
                ctaText: editing.ctaText,
                ctaLink: editing.ctaLink,
                sortOrder: editing.sortOrder,
                active: editing.active,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/hero-slides/')({
  component: HeroSlidesPage,
})
